import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import authService from "../services/discordAuth";
import EmbedBuilder from "../components/EmbedBuilder/EmbedBuilder";
import StatusModal from "../components/StatusModal";

import usePageTitle from "../hooks/usePageTitle";
import "./BotDashboard.css";

function BotDashboard() {
  const { botId, guildId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [definition, setDefinition] = useState(null);
  usePageTitle(t("dashboard.page_title", { botName: definition?.bot_name || botId }));
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [activeSection, setActiveSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [guildChannels, setGuildChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [placeholdersOpen, setPlaceholdersOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({ type: "success", title: "", message: "" });

  const showStatus = (type, message, title) => {
    setModalConfig({ type, message, title });
    setModalOpen(true);
  };

  const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  // Dados reais do usuário logado (já estão no authService depois do login)
  const currentUser = authService.user;

  // Busca lista de canais do servidor para os campos channel_select
  const fetchGuildChannels = async () => {
    if (guildChannels.length > 0 || loadingChannels) return; // já carregou
    setLoadingChannels(true);
    try {
      const response = await authService.backendFetch(`/api/dashboard/channels/${guildId}`);
      if (response && response.ok) {
        const data = await response.json();
        if (data.success) setGuildChannels(data.data);
      }
    } catch (e) {
      console.warn("Não foi possível carregar canais:", e);
    } finally {
      setLoadingChannels(false);
    }
  };

  // Carregamento real via API
  useEffect(() => {
    let mounted = true;
    const fetchDashboardData = async () => {
      setLoading(true);
      setHasError(false);
      try {
        const response = await authService.backendFetch(`/api/dashboard/${botId}/${guildId}`);

        if (!mounted) return;

        if (!response || !response.ok) {
          setHasError(true);
          const data = response ? await response.json() : { error: t("dashboard.messages.generic_conn_error") };
          showStatus("error", data.error || t("dashboard.messages.load_error"));
          navigate(`/bots/${botId}`);
          return;
        }

        const data = await response.json();

        if (data.success) {
          setDefinition(data.data.definition);
          setSettings(data.data.settings || {});
          setInitialSettings(data.data.settings || {});
          // Se já houver algum campo channel_select, pré-carrega os canais
          const hasChannelSelect = data.data.definition?.sections?.some((s) => s.fields?.some((f) => f.type === "channel_select"));
          if (hasChannelSelect) fetchGuildChannels();
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error("Erro ao carregar dashboard:", error);
        if (mounted) {
          setHasError(true);
          showStatus("error", t("dashboard.messages.connection_error"));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (botId && guildId) {
      fetchDashboardData();
    }

    return () => {
      mounted = false;
    };
  }, [botId, guildId, navigate]);

  const handleFieldChange = (fieldId, value) => {
    setSettings((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authService.backendFetch(`/api/dashboard/${botId}/${guildId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings }),
      });

      if (!response || !response.ok) {
        const data = response ? await response.json() : { error: t("dashboard.messages.generic_conn_error") };
        showStatus("error", data.error || t("dashboard.messages.save_error"));
        return;
      }

      const data = await response.json();

      if (data.success) {
        setInitialSettings(settings);
        showStatus("success", t("dashboard.messages.save_success"));
      }
    } catch (error) {
      showStatus("error", t("dashboard.messages.save_conn_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(initialSettings);
  };

  const [testing, setTesting] = useState(null); // ID do campo sendo testado

  const handleTestLog = async (logType, fieldId) => {
    const channelId = settings.logs_channel_id;

    if (!channelId) {
      showStatus("warning", t("dashboard.messages.test_warning"));
      return;
    }

    setTesting(fieldId);
    try {
      const response = await authService.backendFetch(`/api/dashboard/test-log/${botId}/${guildId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          logType,
          messageData: settings[fieldId],
          channelId,
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Opcional: mostrar um feedback visual temporário
      } else {
        showStatus("error", data.error || t("dashboard.messages.test_error"));
      }
    } catch (err) {
      console.error("Test log error:", err);
      showStatus("error", t("dashboard.messages.test_conn_error"));
    } finally {
      setTesting(null);
    }
  };

  if (loading || hasError || !definition) {
    return (
      <div className="bot-dashboard-page">
        <div className="container">
          <div className="dashboard-header">
            <div className="skeleton h-1 bg-gray-800 w-1/4 mx-auto rounded mb-4"></div>
            <div className="skeleton h-0.5 bg-gray-800 w-1/3 mx-auto rounded"></div>
          </div>
          <div className="dashboard-content">
            <div className="sidebar-card skeleton h-64 bg-gray-800 rounded-lg"></div>
            <div className="dashboard-form-container skeleton h-96 bg-gray-800 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const currentSection = definition.sections[activeSection];

  /* ── Renderiza um campo filho dentro de um collapsible_group ── */
  const renderChildField = (child) => (
    <div key={child.id} className="config-group config-group--child">
      <div className="config-label-container">
        <label className="config-label" htmlFor={child.id}>
          {t(child.label)}
        </label>
        {(child.type === "boolean" || child.type === "switch") && (
          <div className="toggle-switch">
            <input type="checkbox" id={child.id} checked={settings[child.id] || false} onChange={(e) => handleFieldChange(child.id, e.target.checked)} />
            <label className="toggle-slider" htmlFor={child.id} />
          </div>
        )}
      </div>
      {child.type === "textarea" && (
        <textarea
          className="dashboard-textarea"
          id={child.id}
          rows={3}
          value={settings[child.id] || ""}
          placeholder={t(child.placeholder) || ""}
          onChange={(e) => handleFieldChange(child.id, e.target.value)}
        />
      )}
      {child.type === "embed_builder" && (
        <>
          <EmbedBuilder
            value={
              typeof settings[child.id] === "object" && settings[child.id] !== null
                ? settings[child.id]
                : {
                    content: settings[child.id] || "",
                    embed: { enabled: false },
                  }
            }
            onChange={(val) => handleFieldChange(child.id, val)}
          />

          <div className="test-action-container">
            <button className={`test-message-btn ${testing === child.id ? "testing" : ""}`} onClick={() => handleTestLog(child.label, child.id)} disabled={testing !== null}>
              <i className="fas fa-paper-plane"></i>
              {testing === child.id ? t("dashboard.fields.test_button_sending") : t("dashboard.fields.test_button")}
            </button>
            <p className="test-help-text">{t("dashboard.fields.test_help")}</p>
          </div>
        </>
      )}
      {child.type === "channel_select" && (
        <select className="dashboard-select" id={child.id} value={settings[child.id] || ""} onChange={(e) => handleFieldChange(child.id, e.target.value)}>
          <option value="">{t("dashboard.fields.channel_default")}</option>
          {guildChannels.map((ch) => (
            <option key={ch.id} value={ch.id}>
              # {ch.name}
            </option>
          ))}
        </select>
      )}
      {child.description && <span className="help-text">{t(child.description)}</span>}
    </div>
  );

  return (
    <div className="bot-dashboard-page">
      <div className="container">
        {/* Header no estilo do EditBotPage */}
        <div className="dashboard-header">
          <h1>{t("dashboard.header.title", { botName: definition.bot_name })}</h1>
          <p>{t("dashboard.header.description", { botName: definition.bot_name })}</p>
        </div>

        <div className="dashboard-content">
          {/* Sidebar Lateral (Estilo Steps) */}
          <aside className="dashboard-sidebar">
            <div className="sidebar-card">
              {definition.sections.map((section, index) => (
                <div key={section.id} className={`section-item ${activeSection === index ? "active" : ""}`} onClick={() => setActiveSection(index)}>
                  <div className="section-number">{section.icon || index + 1}</div>
                  <div className="section-info">
                    <div className="section-title">{t(section.title)}</div>
                    <div className="section-subtitle">
                      {t("dashboard.sidebar.module_prefix")} {index + 1}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* Área Principal de Configurações */}
          <main className="dashboard-form-container">
            <h2>{t(currentSection.title)}</h2>
            {currentSection.description && <p className="section-description">{t(currentSection.description)}</p>}

            <div className="fields-list">
              {currentSection.fields.map((field) =>
                field.type === "collapsible_group" ? (
                  <div key={field.id} className="log-card">
                    <button className="log-card-header" onClick={() => toggleGroup(field.id)}>
                      <span className="log-card-title">
                        {field.icon && <span className="log-card-icon">{field.icon}</span>}
                        {t(field.label)}
                      </span>
                      <span className="log-card-meta">
                        <span className={`log-card-badge ${settings[`${field.id}_enabled`] ? "log-card-badge--on" : "log-card-badge--off"}`}>
                          {settings[`${field.id}_enabled`] ? t("dashboard.status.active") : t("dashboard.status.inactive")}
                        </span>
                        <span className="log-card-chevron">{openGroups[field.id] ? "▲" : "▼"}</span>
                      </span>
                    </button>
                    {openGroups[field.id] && (
                      <div className="log-card-body">
                        {field.description && <p className="log-card-desc">{t(field.description)}</p>}
                        {field.children.map((child) => renderChildField(child))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div key={field.id} className="config-group">
                    <div className="config-label-container">
                      <label className="config-label" htmlFor={field.id}>
                        {t(field.label)}
                      </label>
                      {(field.type === "switch" || field.type === "boolean") && (
                        <div className="toggle-switch">
                          <input type="checkbox" id={field.id} checked={settings[field.id] || false} onChange={(e) => handleFieldChange(field.id, e.target.checked)} />
                          <label className="toggle-slider" htmlFor={field.id}></label>
                        </div>
                      )}
                    </div>

                    {field.type === "text" && (
                      <input
                        className="dashboard-input"
                        type="text"
                        id={field.id}
                        value={settings[field.id] || ""}
                        placeholder={t(field.placeholder) || ""}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      />
                    )}

                    {field.type === "textarea" && (
                      <textarea
                        className="dashboard-textarea"
                        id={field.id}
                        rows={4}
                        value={settings[field.id] || ""}
                        placeholder={t(field.placeholder) || ""}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      />
                    )}

                    {field.type === "select" && (
                      <select className="dashboard-select" id={field.id} value={settings[field.id] || ""} onChange={(e) => handleFieldChange(field.id, e.target.value)}>
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(opt.label)}
                          </option>
                        ))}
                      </select>
                    )}

                    {field.type === "embed_builder" && (
                      <EmbedBuilder
                        value={
                          typeof settings[field.id] === "object" && settings[field.id] !== null
                            ? settings[field.id]
                            : {
                                content: settings[field.id] || "",
                                embed: { enabled: false },
                              }
                        }
                        onChange={(val) => handleFieldChange(field.id, val)}
                      />
                    )}

                    {field.type === "channel_select" && (
                      <select className="dashboard-select" id={field.id} value={settings[field.id] || ""} onChange={(e) => handleFieldChange(field.id, e.target.value)}>
                        <option value="">{loadingChannels ? t("dashboard.fields.loading_channels") : t("dashboard.fields.select_channel")}</option>
                        {guildChannels.map((ch) => (
                          <option key={ch.id} value={ch.id}>
                            # {ch.name}
                          </option>
                        ))}
                        {/* Fallback: se o canal salvo não estiver na lista, mostra ele mesmo */}
                        {settings[field.id] && !guildChannels.find((c) => c.id === settings[field.id]) && (
                          <option value={settings[field.id]}>{t("dashboard.fields.fallback_id", { id: settings[field.id] })}</option>
                        )}
                      </select>
                    )}

                    {field.type === "color" && (
                      <div className="color-field-container">
                        <input
                          type="color"
                          id={field.id}
                          className="dashboard-color-input"
                          value={settings[field.id] || "#5865f2"}
                          onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        />
                        <input
                          className="dashboard-input"
                          type="text"
                          value={settings[field.id] || ""}
                          placeholder={t(field.placeholder) || "#HEX"}
                          onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        />
                      </div>
                    )}

                    {field.description && <span className="help-text">{t(field.description)}</span>}
                  </div>
                ),
              )}
            </div>
          </main>
        </div>

        {/* Card de Placeholders — usa o mesmo grid para alinhar com dashboard-form-container */}
        {currentSection.fields.some((f) => f.type === "embed_builder") && (
          <div className="dashboard-content" style={{ marginTop: "1rem" }}>
            <div /> {/* coluna do aside — vazia, só para manter o alinhamento */}
            <div className="placeholders-card">
              <button className="placeholders-card-header" onClick={() => setPlaceholdersOpen((o) => !o)}>
                <span>{t("dashboard.placeholders.button")}</span>
                <span className="placeholders-chevron">{placeholdersOpen ? "\u2227" : "\u2228"}</span>
              </button>

              {placeholdersOpen && (
                <div className="placeholders-table-wrapper">
                  <table className="placeholders-table">
                    <thead>
                      <tr>
                        <th>{t("dashboard.placeholders.th_placeholder")}</th>
                        <th>{t("dashboard.placeholders.th_value")}</th>
                        <th>{t("dashboard.placeholders.th_description")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        {
                          ph: "{@user}",
                          val: currentUser ? `@${currentUser.username}` : `@${t("dashboard.placeholders.rows.default_member")}`,
                          desc: t("dashboard.placeholders.rows.user_mention"),
                        },
                        {
                          ph: "{user.name}, {user}",
                          val: currentUser?.username ?? t("dashboard.placeholders.rows.default_member"),
                          desc: t("dashboard.placeholders.rows.user_name"),
                        },
                        {
                          ph: "{user.avatar}",
                          val: currentUser ? `https://cdn.discordapp.com/avatars/${currentUser.id}/${currentUser.avatar}.gif?size=64` : "https://cdn.discordapp.com/...",
                          desc: t("dashboard.placeholders.rows.user_avatar"),
                        },
                        {
                          ph: "{user.id}",
                          val: currentUser?.id ?? "000000000000000000",
                          desc: t("dashboard.placeholders.rows.user_id"),
                        },
                        {
                          ph: "{user.discriminator}",
                          val: currentUser?.discriminator ?? "0000",
                          desc: t("dashboard.placeholders.rows.user_discriminator"),
                        },
                        {
                          ph: "{user.tag}",
                          val: currentUser ? `@${currentUser.username}` : `@${t("dashboard.placeholders.rows.default_member").toLowerCase()}`,
                          desc: t("dashboard.placeholders.rows.user_tag"),
                        },
                        {
                          ph: "{guild.name}, {guild}",
                          val: definition?.guild_name ?? t("dashboard.placeholders.rows.default_server"),
                          desc: t("dashboard.placeholders.rows.guild_name"),
                        },
                        {
                          ph: "{guild.size}",
                          val: definition?.guild_member_count ?? "—",
                          desc: t("dashboard.placeholders.rows.guild_size"),
                        },
                        {
                          ph: "{guild.icon}",
                          val: definition?.guild_icon ? `https://cdn.discordapp.com/icons/${guildId}/${definition.guild_icon}.png` : "https://cdn.discordapp.com/icons/...",
                          desc: t("dashboard.placeholders.rows.guild_icon"),
                        },
                        {
                          ph: "{memberCount}",
                          val: definition?.guild_member_count ?? "—",
                          desc: t("dashboard.placeholders.rows.member_count"),
                        },
                      ].map((row) => (
                        <tr key={row.ph}>
                          <td>
                            <code>{row.ph}</code>
                          </td>
                          <td className="ph-val">{row.val}</td>
                          <td>{row.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Barra de Salvamento Flutuante (Fiel ao padrão Discord/BotGate) */}
        <div className={`sticky-save-bar ${hasChanges ? "visible" : ""}`}>
          <span className="save-text">{t("dashboard.save_bar.unsaved_changes")}</span>
          <div className="save-actions">
            <button className="btn btn-secondary" onClick={handleReset}>
              {t("dashboard.save_bar.discard")}
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t("dashboard.save_bar.saving") : t("dashboard.save_bar.save")}
            </button>
          </div>
        </div>
      </div>

      <StatusModal isOpen={modalOpen} onClose={() => setModalOpen(false)} type={modalConfig.type} title={modalConfig.title} message={modalConfig.message} />
    </div>
  );
}

export default BotDashboard;
